/**
 * Technical Requisition Log — official Material / Equipment Request Form + job log.
 * View: project team. Edit: technical coordinators.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import AuditLogModal from '../../common/AuditLogModal';

const API = '/technical-requisitions';
const ACCENT = '#0f4c75';

const STATUS_COLORS = {
  Draft: '#6c757d',
  Submitted: '#0d6efd',
  Issued: '#6f42c1',
  Requested: '#fd7e14',
  'Partially Received': '#ffc107',
  Received: '#198754',
  Installed: '#0f766e',
  Void: '#adb5bd',
};

const BTN = {
  padding: '8px 14px',
  backgroundColor: 'white',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};
const inputStyle = {
  padding: '6px 10px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '0.85rem',
  width: '100%',
  boxSizing: 'border-box',
};

const emptyLine = () => ({
  size_text: '', ident_code: '', tag_number: '', description: '',
  qty_requested: '', uom: 'ea', qty_received: '', on_site_note: '', notes: '',
});

const emptyForm = (defaults = {}) => ({
  title: '',
  status: 'Draft',
  category: 'Material',
  discipline: '',
  client_name: defaults.client_name || '',
  work_area: '',
  contract_number: defaults.job_number || '',
  requested_by: '',
  issued_by: '',
  request_date: new Date().toISOString().slice(0, 10),
  required_date: '',
  transmittal_no: '',
  transmittal_date: '',
  received_date: '',
  vendor: '',
  cost_code: '',
  location: '',
  notes: '',
  bring_to_office: false,
  lines: [emptyLine()],
});

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-CA');
  } catch {
    return String(v);
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ status }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    background: STATUS_COLORS[status] || '#9ca3af',
    color: '#fff',
  }}
  >
    {status || '—'}
  </span>
);

const Field = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const RequisitionLogPage = ({ projectId, projectDetails }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalRequisitions', projectId || 'global', true);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], categories: [], disciplines: [], can_edit: false });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [toast, setToast] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [saving, setSaving] = useState(false);

  const [showHelp, setShowHelp] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailId, setEmailId] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const importRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchList = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const [listRes, metaRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}`),
        authenticatedApiCall(instance, accounts, 'GET', `${API}/meta`),
      ]);
      if (listRes.data?.success) setRows(listRes.data.data || []);
      if (metaRes.data?.success) {
        setMeta({
          ...metaRes.data.data,
          can_edit: listRes.data?.can_edit ?? metaRes.data.data.can_edit,
        });
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to load requisitions', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const canEdit = !!meta.can_edit;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      if (!q) return true;
      return [
        r.req_number, r.title, r.status, r.category, r.discipline, r.requested_by,
        r.issued_by, r.work_area, r.transmittal_no, r.vendor, r.notes,
      ].some((v) => String(v || '').toLowerCase().includes(q));
    });
    list = [...list].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [rows, search, statusFilter, sortKey, sortDir]);

  const kpis = useMemo(() => {
    const open = rows.filter((r) => !['Received', 'Installed', 'Void'].includes(r.status)).length;
    const received = rows.filter((r) => r.status === 'Received' || r.status === 'Installed').length;
    const overdue = rows.filter((r) => {
      if (!r.required_date || ['Received', 'Installed', 'Void'].includes(r.status)) return false;
      return new Date(r.required_date) < new Date();
    }).length;
    return { total: rows.length, open, received, overdue };
  }, [rows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm({
      client_name: projectDetails?.client_name || '',
      job_number: projectDetails?.job_number || '',
    }));
    setShowForm(true);
  };

  const openEdit = async (row) => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/detail/${row.id}`);
      const data = r.data?.data || row;
      setEditingId(data.id);
      setForm({
        ...emptyForm(),
        ...data,
        request_date: data.request_date ? String(data.request_date).slice(0, 10) : '',
        required_date: data.required_date ? String(data.required_date).slice(0, 10) : '',
        transmittal_date: data.transmittal_date ? String(data.transmittal_date).slice(0, 10) : '',
        received_date: data.received_date ? String(data.received_date).slice(0, 10) : '',
        lines: (data.lines && data.lines.length) ? data.lines : [emptyLine()],
      });
      setShowForm(true);
    } catch (err) {
      showToast('Failed to open requisition', 'error');
    }
  };

  const saveForm = async () => {
    if (!form.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lines: form.lines };
      const path = editingId
        ? `${API}/project/${projectId}/${editingId}`
        : `${API}/project/${projectId}`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await authenticatedApiCall(instance, accounts, method, path, payload);
      if (r.data?.success) {
        showToast(editingId ? 'Requisition saved' : `Created ${r.data.data.req_number}`);
        setShowForm(false);
        fetchList();
      } else {
        showToast(r.data?.message || 'Save failed', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete ${row.req_number}? This stays in the audit log.`)) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/project/${projectId}/${row.id}`);
      if (r.data?.success) {
        showToast(`Deleted ${row.req_number}`);
        fetchList();
      } else showToast(r.data?.message || 'Delete failed', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const downloadNamed = async (path, fallback) => {
    const r = await authenticatedApiCall(instance, accounts, 'GET', path, null, { responseType: 'blob' });
    const cd = r.headers?.['content-disposition'] || '';
    const match = cd.match(/filename="([^"]+)"/);
    downloadBlob(r.data, match?.[1] || fallback);
  };

  const handleExport = async () => {
    try {
      await downloadNamed(`${API}/project/${projectId}/export`, 'requisition-log.xlsx');
      showToast('Exported to Excel');
    } catch {
      showToast('Export failed', 'error');
    }
  };

  const handleTemplate = async () => {
    try {
      await downloadNamed(`${API}/project/${projectId}/template`, 'requisition-template.xlsx');
    } catch {
      showToast('Template download failed', 'error');
    }
  };

  const handlePdf = async (id) => {
    try {
      const path = id
        ? `${API}/project/${projectId}/${id}/pdf`
        : `${API}/project/${projectId}/pdf`;
      await downloadNamed(path, id ? 'requisition.pdf' : 'requisition-log.pdf');
    } catch {
      showToast('PDF failed', 'error');
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/import`, fd);
      if (r.data?.success) {
        showToast(r.data.message || 'Imported');
        setShowImport(false);
        fetchList();
      } else showToast(r.data?.message || 'Import failed', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };

  const handleEmail = async () => {
    setEmailSending(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/email`, {
        to: emailTo,
        requisitionId: emailId,
      });
      if (r.data?.success) {
        showToast(r.data.message);
        setShowEmail(false);
        setEmailTo('');
        setEmailId(null);
      } else showToast(r.data?.message || 'Email failed', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'Email failed', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const openAnalytics = async () => {
    setShowAnalytics(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/analytics`);
      if (r.data?.success) setAnalytics(r.data.data);
    } catch {
      showToast('Analytics failed', 'error');
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const setLine = (i, patch) => {
    setForm((prev) => {
      const lines = prev.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
      return { ...prev, lines };
    });
  };

  const columns = [
    { key: 'req_number', label: 'Req #', width: 90 },
    { key: 'title', label: 'Title', width: 220 },
    { key: 'status', label: 'Status', width: 130 },
    { key: 'category', label: 'Category', width: 110 },
    { key: 'discipline', label: 'Discipline', width: 110 },
    { key: 'requested_by', label: 'Requested By', width: 140 },
    { key: 'request_date', label: 'Req Date', width: 110 },
    { key: 'required_date', label: 'Needed By', width: 110 },
    { key: 'transmittal_no', label: 'Trans. #', width: 90 },
    { key: 'line_count', label: 'Lines', width: 70 },
  ];

  return (
    <div style={{ padding: '1.25rem 1.5rem' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 12000,
          padding: '10px 14px', borderRadius: 8, color: '#fff',
          background: toast.type === 'error' ? '#dc2626' : '#15803d',
        }}
        >
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Requisition Log</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13 }}>
            Official material / equipment request form. Use this instead of Word emails.
            {canEdit ? ' You can edit.' : ' View only — technical team maintains the log.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus />
          <button type="button" style={BTN} onClick={() => setShowHelp(true)}>Help</button>
          <button type="button" style={BTN} onClick={() => setShowAudit(true)}>Audit Log</button>
          <button type="button" style={BTN} onClick={openAnalytics}>Analytics</button>
          {canEdit && <button type="button" style={BTN} onClick={() => setShowImport(true)}>Import</button>}
          <button type="button" style={BTN} onClick={handleExport}>Excel</button>
          <button type="button" style={BTN} onClick={() => handlePdf(null)}>PDF</button>
          <button type="button" style={BTN} onClick={() => { setEmailId(null); setShowEmail(true); }}>Email</button>
          {canEdit && (
            <button type="button" style={{ ...BTN, background: ACCENT, color: '#fff', borderColor: ACCENT }} onClick={openCreate}>
              + New Requisition
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Total', value: kpis.total, color: ACCENT },
          { label: 'Open', value: kpis.open, color: '#c2410c' },
          { label: 'Received / Installed', value: kpis.received, color: '#15803d' },
          { label: 'Past needed-by', value: kpis.overdue, color: kpis.overdue ? '#b91c1c' : '#64748b' },
        ].map((k) => (
          <div key={k.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search requisitions…" style={{ ...inputStyle, width: 260 }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: 180 }}>
          <option value="All">All statuses</option>
          {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ alignSelf: 'center', fontSize: 13, color: '#64748b' }}>{filtered.length} shown</span>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <div style={{ overflow: 'auto', maxHeight: 480 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    style={{
                      textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0',
                      cursor: 'pointer', whiteSpace: 'nowrap', minWidth: c.width,
                    }}
                  >
                    {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th style={{ width: 160, padding: '8px 10px', borderBottom: '2px solid #e2e8f0' }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} style={{ padding: 28, textAlign: 'center', color: '#64748b' }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} style={{ padding: 28, textAlign: 'center', color: '#64748b' }}>
                    No requisitions yet. {canEdit ? 'Create one instead of emailing a Word file.' : 'Ask the technical team to add the first one.'}
                  </td>
                </tr>
              ) : filtered.map((row, idx) => {
                const overdue = row.required_date && !['Received', 'Installed', 'Void'].includes(row.status) && new Date(row.required_date) < new Date();
                return (
                  <tr key={row.id} style={{ background: overdue ? '#fff5f5' : idx % 2 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 600, color: ACCENT }}>{row.req_number}</td>
                    <td style={{ padding: '8px 10px' }}>{row.title}</td>
                    <td style={{ padding: '8px 10px' }}><StatusBadge status={row.status} /></td>
                    <td style={{ padding: '8px 10px' }}>{row.category || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{row.discipline || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{row.requested_by || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{fmtDate(row.request_date)}</td>
                    <td style={{ padding: '8px 10px', color: overdue ? '#b91c1c' : undefined, fontWeight: overdue ? 600 : 400 }}>{fmtDate(row.required_date)}</td>
                    <td style={{ padding: '8px 10px' }}>{row.transmittal_no || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{row.line_count ?? 0}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <button type="button" style={{ ...BTN, padding: '4px 8px' }} onClick={() => openEdit(row)}>{canEdit ? 'Open' : 'View'}</button>
                      <button type="button" style={{ ...BTN, padding: '4px 8px', marginLeft: 4 }} onClick={() => handlePdf(row.id)}>PDF</button>
                      {canEdit && (
                        <button type="button" style={{ ...BTN, padding: '4px 8px', marginLeft: 4, color: '#b91c1c' }} onClick={() => deleteRow(row)}>Del</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && ReactDOM.createPortal(
        <div onClick={() => !saving && setShowForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 920, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto', padding: 20, boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px' }}>{editingId ? `Requisition ${form.req_number || ''}` : 'New Material / Equipment Request'}</h3>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>Same fields as the LGL request form (Req #993) and the Pratt Paper log.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Title *" span>
                <input style={inputStyle} value={form.title} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. FG-04 skid valves" />
              </Field>
              <Field label="Status">
                <select style={inputStyle} value={form.status} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                  {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select style={inputStyle} value={form.category} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                  {(meta.categories || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Discipline">
                <select style={inputStyle} value={form.discipline} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, discipline: e.target.value }))}>
                  <option value="">—</option>
                  {(meta.disciplines || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Client"><input style={inputStyle} value={form.client_name} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))} /></Field>
              <Field label="Work Area"><input style={inputStyle} value={form.work_area} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, work_area: e.target.value }))} /></Field>
              <Field label="Contract #"><input style={inputStyle} value={form.contract_number} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, contract_number: e.target.value }))} /></Field>
              <Field label="Requested By"><input style={inputStyle} value={form.requested_by} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, requested_by: e.target.value }))} /></Field>
              <Field label="Issued By"><input style={inputStyle} value={form.issued_by} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, issued_by: e.target.value }))} /></Field>
              <Field label="Request Date"><input type="date" style={inputStyle} value={form.request_date || ''} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, request_date: e.target.value }))} /></Field>
              <Field label="Needed By"><input type="date" style={inputStyle} value={form.required_date || ''} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, required_date: e.target.value }))} /></Field>
              <Field label="Transmittal #"><input style={inputStyle} value={form.transmittal_no} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, transmittal_no: e.target.value }))} /></Field>
              <Field label="Transmittal Date"><input type="date" style={inputStyle} value={form.transmittal_date || ''} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, transmittal_date: e.target.value }))} /></Field>
              <Field label="Received Date"><input type="date" style={inputStyle} value={form.received_date || ''} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, received_date: e.target.value }))} /></Field>
              <Field label="Vendor"><input style={inputStyle} value={form.vendor} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))} /></Field>
              <Field label="Cost Code"><input style={inputStyle} value={form.cost_code} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, cost_code: e.target.value }))} /></Field>
              <Field label="Location"><input style={inputStyle} value={form.location} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} /></Field>
              <Field label="Notes" span>
                <textarea style={{ ...inputStyle, minHeight: 56 }} value={form.notes} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={!!form.bring_to_office} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, bring_to_office: e.target.checked }))} />
                Bring to office
              </label>
            </div>

            <h4 style={{ margin: '16px 0 8px' }}>Line items</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {['Size', 'Ident Code', 'Tag #', 'Ident Description', 'Qty', 'UOM', 'On Site', ''].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, i) => (
                    <tr key={i}>
                      <td style={{ padding: 4 }}><input style={inputStyle} value={line.size_text} disabled={!canEdit} onChange={(e) => setLine(i, { size_text: e.target.value })} /></td>
                      <td style={{ padding: 4 }}><input style={inputStyle} value={line.ident_code} disabled={!canEdit} onChange={(e) => setLine(i, { ident_code: e.target.value })} /></td>
                      <td style={{ padding: 4 }}><input style={inputStyle} value={line.tag_number} disabled={!canEdit} onChange={(e) => setLine(i, { tag_number: e.target.value })} /></td>
                      <td style={{ padding: 4, minWidth: 180 }}><input style={inputStyle} value={line.description} disabled={!canEdit} onChange={(e) => setLine(i, { description: e.target.value })} /></td>
                      <td style={{ padding: 4, width: 70 }}><input style={inputStyle} value={line.qty_requested} disabled={!canEdit} onChange={(e) => setLine(i, { qty_requested: e.target.value })} /></td>
                      <td style={{ padding: 4, width: 60 }}><input style={inputStyle} value={line.uom} disabled={!canEdit} onChange={(e) => setLine(i, { uom: e.target.value })} /></td>
                      <td style={{ padding: 4 }}><input style={inputStyle} value={line.on_site_note} disabled={!canEdit} onChange={(e) => setLine(i, { on_site_note: e.target.value })} /></td>
                      <td style={{ padding: 4 }}>
                        {canEdit && form.lines.length > 1 && (
                          <button type="button" style={{ ...BTN, padding: '4px 8px' }} onClick={() => setForm((p) => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }))}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canEdit && (
              <button type="button" style={{ ...BTN, marginTop: 8 }} onClick={() => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }))}>+ Line</button>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              {editingId && <button type="button" style={BTN} onClick={() => handlePdf(editingId)}>PDF</button>}
              {editingId && <button type="button" style={BTN} onClick={() => { setEmailId(editingId); setShowEmail(true); }}>Email</button>}
              <button type="button" style={BTN} onClick={() => setShowForm(false)}>Close</button>
              {canEdit && (
                <button type="button" style={{ ...BTN, background: ACCENT, color: '#fff', borderColor: ACCENT }} disabled={saving} onClick={saveForm}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showImport && ReactDOM.createPortal(
        <div onClick={() => !importing && setShowImport(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 460 }}>
            <h3 style={{ margin: '0 0 8px' }}>Import requisitions</h3>
            <p style={{ fontSize: 13, color: '#475569' }}>
              Use the template (two sheets: Requisition Log and Lines). Existing Req # rows are updated.
            </p>
            <button type="button" style={{ ...BTN, marginBottom: 12 }} onClick={handleTemplate}>Download template</button>
            <input ref={importRef} type="file" accept=".xlsx,.xls" onChange={(e) => handleImport(e.target.files?.[0])} />
            {importing && <p style={{ color: ACCENT }}>Importing…</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" style={BTN} onClick={() => setShowImport(false)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showEmail && ReactDOM.createPortal(
        <div onClick={() => !emailSending && setShowEmail(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 440 }}>
            <h3 style={{ margin: '0 0 8px' }}>Email PDF</h3>
            <p style={{ fontSize: 13, color: '#475569' }}>
              {emailId ? 'Sends the official request form.' : 'Sends the job requisition log.'} Enter addresses separated by commas.
            </p>
            <input style={inputStyle} value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="name@laframboise.net, client@owner.com" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button type="button" style={BTN} onClick={() => setShowEmail(false)}>Cancel</button>
              <button type="button" style={{ ...BTN, background: ACCENT, color: '#fff', borderColor: ACCENT }} disabled={emailSending} onClick={handleEmail}>
                {emailSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showAnalytics && ReactDOM.createPortal(
        <div onClick={() => setShowAnalytics(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 860, maxWidth: '96vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Requisition analytics</h3>
              <button type="button" style={BTN} onClick={() => setShowAnalytics(false)}>Close</button>
            </div>
            {!analytics ? <p>Loading…</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                <div>
                  <h4 style={{ margin: '0 0 8px' }}>By status</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={analytics.byStatus} dataKey="count" nameKey="status" outerRadius={80} label>
                        {(analytics.byStatus || []).map((s) => (
                          <Cell key={s.status} fill={STATUS_COLORS[s.status] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 style={{ margin: '0 0 8px' }}>By category</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.byCategory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill={ACCENT} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {showHelp && ReactDOM.createPortal(
        <div onClick={() => setShowHelp(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 720, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Requisition Log Help</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="requisition-log-help" title="Requisition Log Help" accent={ACCENT} />
                <button type="button" onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 16, overflow: 'auto' }}>
              <HelpWithVideoTour guideId="requisition-log-help" title="Requisition Log Help" accent={ACCENT}>
                <div style={{ padding: 12, background: '#e0f2fe', borderRadius: 8, marginBottom: 14 }}>
                  <strong>Why this exists.</strong> Requisitions used to live in Word emails and one-off Excel files. Those have no number, no status, and no history. This page is the official Material / Equipment Request Form and the job log together.
                </div>
                {[
                  ['The form', 'Same header as the old request sheet: client, contract, requested by, issued by, work area, needed-by, transmittal, and line items (size, ident code, tag, quantity, unit, on site).'],
                  ['Numbering', 'Each job gets REQ-001, REQ-002, and so on. You can still type a client number if they already issued one.'],
                  ['Who can edit', 'Everyone on the project team can open and export. Only the technical team (technical coordinators) can create, edit, import, or delete.'],
                  ['Export / Import / PDF / Email', 'Excel downloads the log plus line items. Import accepts that same file. PDF prints the letterhead form or the full log. Email attaches that PDF — use it instead of a Word attachment.'],
                  ['Status', 'Draft, Submitted, Issued, Requested, Partially Received, Received, Installed, Void. The Pratt Paper log used Requested / Received / Installed; those still work here.'],
                  ['Audit', 'Every create, edit, delete, import, export, and email is written to the audit log.'],
                ].map(([title, desc]) => (
                  <div key={title} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 14, color: '#334155' }}>{desc}</div>
                  </div>
                ))}
              </HelpWithVideoTour>
            </div>
          </div>
        </div>,
        document.body
      )}

      <AuditLogModal
        isOpen={showAudit}
        onClose={() => setShowAudit(false)}
        endpoint={`${API}/project/${projectId}/audit`}
        title="Requisition Audit Log"
        accentColor={ACCENT}
      />
    </div>
  );
};

export default RequisitionLogPage;
